import React, { useEffect, useRef, useState } from 'react';
import { Box, Grow, Paper, TextField } from '@mui/material';
import { showNotificationBar } from '../../../actions/error-pages/NotificationBarActions';
import { setUserFromCognito } from '../../../actions/profile/UserActions';
import { logProfileIn, resendVerificationCode, verifyCode } from '../../../clients/profile/ProfileClient';
import Colors from '../../../constants/Colors';
import { SESSION_REDIRECT_KEY, getSessionProperty } from '../../../utilities/SessionUtilities';
import { LoadingButton, TextButton } from '../../common/StyledButtons';

const VerificationForm = ({ location, history }) => {
  const [hasResentCode, setHasRecentCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputsRef = useRef([]);
  const userRef = useRef(location.state ? location.state.user : undefined);

  useEffect(() => {
    if (!userRef.current || !userRef.current.email || !userRef.current.password) {
      history.push('/register');
    }
  }, []);

  const handleResendCode = async () => {
    try {
      await resendVerificationCode(userRef.current.email);
      setHasRecentCode(true);
      showNotificationBar('success', 'We just sent you a new code to your email!');
    } catch (err) {
      console.error('resend code error', err);
      showNotificationBar('error', 'An issue happened when sending the verification code.');
    }
  };

  const handleVerify = async () => {
    const code = verificationCode.join('');
    if (!code || code.length !== 6) {
      setErrorMessage('Please enter the 6-digit code.');
      return;
    }

    try {
      const { email, password } = userRef.current;
      setIsLoading(true);
      await verifyCode(email, code);
      await handleSignIn(email, password);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setErrorMessage('Oh no! That code was not correct.');
      } else {
        console.error('verification error', err);
        setErrorMessage('Oh no! Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (email, password) => {
    try {
      const user = await logProfileIn(email.toLowerCase(), password);
      setUserFromCognito(user);
      const redirect = getSessionProperty(SESSION_REDIRECT_KEY, true);
      history.push(redirect || '/dashboard');
    } catch (e) {
      console.error('LOGIN ERROR', e);
      history.push('/login');
    }
  };

  const handleDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    if (value && index < 5) {
      if (inputsRef.current[index + 1]) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').trim();
    if (!/^\d{6}$/.test(paste)) return;
  
    const newValues = paste.split('');
    setVerificationCode(newValues);
  
    // Optionally move focus to the last input
    if (inputsRef.current[5]) {
      inputsRef.current[5].focus();
    }
  };
  

  const handleDigitKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      if (inputsRef.current[index - 1]) {
        inputsRef.current[index - 1].focus();
      }
    }
  };

  const renderVerifyContent = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <h3 style={{ textAlign: 'center', margin: 0, fontSize: '28px' }}>One Last Step to Get Started</h3>
        <p>Please check your email to retrieve your activation code and insert it below.</p>
        {errorMessage && (
          <p style={{ color: Colors.errorOutline, fontSize: '0.875rem', marginTop: '10px' }}>
            {errorMessage}
          </p>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
        {verificationCode.map((digit, i) => (
          <TextField
            key={i}
            inputRef={(el) => (inputsRef.current[i] = el)}
            value={digit}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onPaste={i === 0 ? handlePaste : undefined}
            onKeyDown={(e) => handleDigitKeyDown(e, i)}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '24px',
                width: '40px',
                height: '50px',
                padding: 0,
              },
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TextButton onClick={handleResendCode} disabled={isLoading || hasResentCode}>
          Resend Code
        </TextButton>
        <LoadingButton onClick={handleVerify} disabled={isLoading} isLoading={isLoading}>
          Verify
        </LoadingButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
      }}
    >
      <Grow in timeout={500}>
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: '600px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '32px',
            boxSizing: 'border-box',
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ width: '100%' }}>{renderVerifyContent()}</Box>
        </Paper>
      </Grow>
    </Box>
  );
};

export default VerificationForm;
